import React, { useEffect, useState } from 'react'
import { X, Save, User, Phone, Calendar, Briefcase, CheckCircle, AlertCircle, MapPin } from 'lucide-react'

const FuncionarioModal = ({ isOpen, onClose, funcionario = null, onSave, onDelete, canEdit = true }) => {
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    ativo: 'sim',
    genero: '',
    estado_civil: '',
    data_nascimento: '',
    telefone: '',
    cargo: '',
    observacao: '',
    cep: '',
    logradouro: '',
    bairro: '',
    numero: '',
    uf: '',
    cidade: '',
    regiao: ''
  })
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (funcionario) {
      setFormData({
        nome: funcionario.nome || '',
        email: funcionario.email || '',
        senha: '',
        ativo: funcionario.ativo || 'sim',
        genero: funcionario.genero || '',
        estado_civil: funcionario.estado_civil || '',
        data_nascimento: funcionario.data_nascimento || '',
        telefone: funcionario.telefone || '',
        cargo: funcionario.cargo || '',
        observacao: funcionario.observacao || '',
        cep: funcionario.cep || '',
        logradouro: funcionario.logradouro || '',
        bairro: funcionario.bairro || '',
        numero: funcionario.numero || '',
        uf: funcionario.uf || '',
        cidade: funcionario.cidade || '',
        regiao: funcionario.regiao || ''
      })
    } else {
      setFormData({
        nome: '',
        email: '',
        senha: '',
        ativo: 'sim',
        genero: '',
        estado_civil: '',
        data_nascimento: '',
        telefone: '',
        cargo: '',
        observacao: '',
        cep: '',
        logradouro: '',
        bairro: '',
        numero: '',
        uf: '',
        cidade: '',
        regiao: ''
      })
    }
    setErrors({})
  }, [funcionario])

  const validateForm = () => {
    const newErrors = {}
    if (!formData.nome.trim()) newErrors.nome = 'Nome é obrigatório'
    if (!formData.email.trim()) newErrors.email = 'Email é obrigatório'
    else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
      if (!emailRegex.test(formData.email)) newErrors.email = 'Email inválido'
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validateForm()) return
    setLoading(true)
    try {
      await onSave(formData)
      onClose()
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClick = async () => {
    if (!funcionario) return
    if (!window.confirm('Tem certeza que deseja excluir este funcionário?')) return
    setLoading(true)
    try {
      await onDelete(funcionario.id_funcionario)
      onClose()
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            {funcionario ? 'Editar Funcionário' : 'Novo Funcionário'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <User className="inline h-4 w-4 mr-2" />
                Nome Completo *
              </label>
              <input type="text" name="nome" value={formData.nome} onChange={handleInputChange} className={`input w-full ${errors.nome ? 'border-red-500' : ''}`} />
              {errors.nome && <p className="text-red-500 text-sm mt-1">{errors.nome}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} className={`input w-full ${errors.email ? 'border-red-500' : ''}`} />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Senha</label>
              <input type="password" name="senha" value={formData.senha} onChange={handleInputChange} className="input w-full" placeholder="Deixe em branco para não alterar" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Ativo</label>
              <select name="ativo" value={formData.ativo} onChange={handleInputChange} className="input w-full">
                <option value="sim">Sim</option>
                <option value="nao">Não</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gênero</label>
              <select name="genero" value={formData.genero} onChange={handleInputChange} className="input w-full">
                <option value="">Selecione...</option>
                <option value="M">Masculino</option>
                <option value="F">Feminino</option>
                <option value="O">Outro</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Estado Civil</label>
              <select name="estado_civil" value={formData.estado_civil} onChange={handleInputChange} className="input w-full">
                <option value="">Selecione...</option>
                <option value="solteiro">Solteiro(a)</option>
                <option value="casado">Casado(a)</option>
                <option value="divorciado">Divorciado(a)</option>
                <option value="viuvo">Viúvo(a)</option>
                <option value="uniao_estavel">União Estável</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Calendar className="inline h-4 w-4 mr-2" />
                Data de Nascimento
              </label>
              <input type="date" name="data_nascimento" value={formData.data_nascimento} onChange={handleInputChange} className="input w-full" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Phone className="inline h-4 w-4 mr-2" />
                Telefone
              </label>
              <input type="text" name="telefone" value={formData.telefone} onChange={handleInputChange} className="input w-full" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Briefcase className="inline h-4 w-4 mr-2" />
                Cargo
              </label>
              <input type="text" name="cargo" value={formData.cargo} onChange={handleInputChange} className="input w-full" />
            </div>
          </div>

          <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
              <MapPin className="inline h-5 w-5 mr-2" />
              Endereço
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">CEP</label>
                <input type="text" name="cep" value={formData.cep} onChange={handleInputChange} className="input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Logradouro</label>
                <input type="text" name="logradouro" value={formData.logradouro} onChange={handleInputChange} className="input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Bairro</label>
                <input type="text" name="bairro" value={formData.bairro} onChange={handleInputChange} className="input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Número</label>
                <input type="text" name="numero" value={formData.numero} onChange={handleInputChange} className="input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">UF</label>
                <input type="text" name="uf" value={formData.uf} onChange={handleInputChange} className="input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Cidade</label>
                <input type="text" name="cidade" value={formData.cidade} onChange={handleInputChange} className="input w-full" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Região</label>
                <input type="text" name="regiao" value={formData.regiao} onChange={handleInputChange} className="input w-full" />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observação</label>
            <textarea name="observacao" value={formData.observacao} onChange={handleInputChange} rows="3" className="input w-full resize-none" />
          </div>

          <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-3">
              {funcionario && canEdit && (
                <button type="button" onClick={handleDeleteClick} disabled={loading} className="btn bg-red-600 hover:bg-red-700 text-white disabled:opacity-50">
                  Excluir
                </button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={onClose} disabled={loading} className="btn-outline">Cancelar</button>
              {canEdit && (
                <button type="submit" disabled={loading} className="btn-primary">
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Salvando...' : 'Salvar'}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default FuncionarioModal